import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Checkbox } from "@/components/ui/checkbox"
import {
  Gamepad2,
  ShieldCheck,
  Crown,
  Sparkles,
  LifeBuoy,
  Headphones,
  MessageSquare,
  Shield,
  Gavel,
  Ban,
  Car,
  Map,
  Server,
  ShoppingBag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import LogoCapital from '../../assets/logo-capital.jpg';

/**
 * Página de login - Autentica usuário via Discord (Único método)
 * Layout modernizado para combinar com o Dashboard
 */
const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [shoppingAgreed, setShoppingAgreed] = useState(false);
  const { loginWithDiscord, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redireciona se já estiver logado
  React.useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Função para iniciar login com Discord
  const handleDiscordLogin = () => {
    // Substitua pelo seu Client ID do Discord
    const CLIENT_ID = '1417186195036967062';
    const REDIRECT_URI = encodeURIComponent(`${window.location.origin}/auth/discord/callback`);

    // URL de autorização do Discord (Implicit Grant)
    // Escopos: identify email guilds guilds.members.read (para pegar o apelido no servidor)
    const discordAuthUrl = `https://discord.com/api/oauth2/authorize?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=token&scope=identify%20email%20guilds%20guilds.members.read`;

    window.location.href = discordAuthUrl;
  };

  // Ícones para o background animado
  const backgroundIcons = [
    { Icon: LifeBuoy, color: "text-blue-500" },
    { Icon: Shield, color: "text-red-500" },
    { Icon: Gamepad2, color: "text-purple-500" },
    { Icon: Headphones, color: "text-cyan-500" },
    { Icon: Gavel, color: "text-orange-500" },
    { Icon: Car, color: "text-yellow-500" },
    { Icon: MessageSquare, color: "text-green-500" },
    { Icon: Ban, color: "text-rose-500" },
    { Icon: Map, color: "text-emerald-500" },
    { Icon: Server, color: "text-indigo-500" },
    { Icon: Crown, color: "text-amber-500" },
    { Icon: Sparkles, color: "text-pink-500" },
  ];

  // Duplicar ícones para loop infinito suave
  const infiniteIcons = [...backgroundIcons, ...backgroundIcons, ...backgroundIcons];

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 overflow-hidden relative selection:bg-blue-500/30">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Orbs */}
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.1),transparent_70%)]" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_80%_80%,rgba(168,85,247,0.1),transparent_70%)]" />

        {/* Scrolling Icons Rows */}
        <div className="absolute inset-0 flex flex-col justify-between opacity-5 select-none rotate-[-12deg] scale-125">
          {/* Row 1 - Left to Right */}
          <motion.div
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            className="flex gap-24 whitespace-nowrap"
          >
            {infiniteIcons.map((item, index) => (
              <item.Icon key={`r1-${index}`} className={`w-24 h-24 ${item.color}`} />
            ))}
          </motion.div>

          {/* Row 2 - Right to Left */}
          <motion.div
            animate={{ x: ["-50%", "0%"] }}
            transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
            className="flex gap-32 whitespace-nowrap ml-12"
          >
            {infiniteIcons.map((item, index) => (
              <item.Icon key={`r2-${index}`} className={`w-20 h-20 ${item.color}`} />
            ))}
          </motion.div>

          {/* Row 3 - Left to Right */}
          <motion.div
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
            className="flex gap-28 whitespace-nowrap"
          >
            {infiniteIcons.map((item, index) => (
              <item.Icon key={`r3-${index}`} className={`w-28 h-28 ${item.color}`} />
            ))}
          </motion.div>

          {/* Row 4 - Right to Left */}
          <motion.div
            animate={{ x: ["-50%", "0%"] }}
            transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
            className="flex gap-36 whitespace-nowrap ml-24"
          >
            {infiniteIcons.map((item, index) => (
              <item.Icon key={`r4-${index}`} className={`w-16 h-16 ${item.color}`} />
            ))}
          </motion.div>
        </div>

        {/* Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#050505_100%)] opacity-80" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5, type: "spring", damping: 20 }}
          className="bg-black/60 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-8 md:p-12 shadow-[0_0_50px_-12px_rgba(0,0,0,0.5)] relative overflow-hidden ring-1 ring-white/5"
        >
          {/* Shine Effect */}
          <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-transparent opacity-50 pointer-events-none" />

          {/* Header */}
          <div className="text-center mb-12 relative">
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mb-8 inline-block relative"
            >
              <div className="w-32 h-32 mx-auto rounded-full flex items-center justify-center mb-4 relative group">
                {/* Animated Ring */}
                <motion.div
                  className="absolute -inset-4 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-75 blur-md"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                />

                {/* Rotating Border */}
                <motion.div
                  className="absolute -inset-[4px] rounded-full border-2 border-transparent border-t-blue-400 border-r-purple-400 border-b-pink-400 border-l-blue-400"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                />

                <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500 opacity-50" />
                <img
                  src={LogoCapital}
                  alt="Grupo Capital Logo"
                  className="w-full h-full object-cover rounded-full relative z-10 border-2 border-white/10 shadow-2xl"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h1 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 bg-gradient-to-r from-blue-500 via-purple-500 to-red-500 bg-clip-text text-transparent tracking-tight whitespace-nowrap pb-2 pr-2">
                GRUPO CAPITAL
              </h1>
              <p className="text-blue-200/60 text-base font-medium tracking-wide max-w-[280px] mx-auto leading-relaxed">
                Acesse seus tickets, solicitações e histórico em um único painel.
              </p>
            </motion.div>
          </div>

          {/* Login Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-6"
          >
            {/* Agreement Checkbox */}
            <div className="flex items-start gap-3 px-1">
              <Checkbox
                id="terms"
                checked={agreed}
                onCheckedChange={setAgreed}
                className="mt-1 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 border-white/20"
              />
              <label
                htmlFor="terms"
                className="text-sm text-gray-400 leading-snug select-none cursor-pointer"
              >
                Li e concordo com os <Link to="/terms" className="text-blue-400 hover:text-blue-300 transition-colors hover:underline">Termos de Serviço</Link> e a <Link to="/privacy" className="text-blue-400 hover:text-blue-300 transition-colors hover:underline">Política de Privacidade</Link> do Grupo Capital.
              </label>
            </div>

            {/* Shopping Terms Checkbox */}
            <div className="flex items-start gap-3 px-1">
              <Checkbox
                id="shopping-terms"
                checked={shoppingAgreed}
                onCheckedChange={setShoppingAgreed}
                className="mt-1 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 border-white/20"
              />
              <label
                htmlFor="shopping-terms"
                className="text-sm text-gray-400 leading-snug select-none cursor-pointer"
              >
                Li e concordo com a <Link to="/shopping-terms" className="text-blue-400 hover:text-blue-300 transition-colors hover:underline">Política de Compras e Doações</Link>.
              </label>
            </div>

            <Button
              onClick={handleDiscordLogin}
              disabled={!agreed || !shoppingAgreed}
              className={`w-full group relative text-white font-semibold text-lg py-7 rounded-2xl transition-all duration-300 overflow-hidden ${agreed && shoppingAgreed
                ? 'bg-[#5865F2] hover:bg-[#4752C4] shadow-[0_10px_30px_-10px_rgba(88,101,242,0.4)] hover:shadow-[0_20px_40px_-10px_rgba(88,101,242,0.6)] hover:-translate-y-1'
                : 'bg-gray-800/50 text-gray-500 cursor-not-allowed'
                }`}
            >
              {(agreed && shoppingAgreed) && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out" />
              )}
              <div className="flex items-center justify-center gap-3 relative z-10">
                <Gamepad2 className={`h-6 w-6 transition-transform duration-300 ${agreed ? 'group-hover:scale-110' : ''}`} />
                <span>Entrar com Discord</span>
              </div>
            </Button>

            <div className="flex items-center justify-center gap-2 text-xs font-medium text-white/30 bg-white/5 py-3 rounded-xl border border-white/5 backdrop-blur-sm">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400/80" />
              <span>Ambiente seguro e autenticado</span>
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mt-12 space-y-4"
          >
            <p className="text-[10px] uppercase tracking-widest text-white/20 flex items-center justify-center gap-2 font-medium">
              <Sparkles className="h-3 w-3 text-yellow-500/40" />
              Sistema oficial do Grupo Capital &copy; 2026
              <Sparkles className="h-3 w-3 text-yellow-500/40" />
            </p>
            <a
              href="https://tronos.space"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-[10px] text-white/10 hover:text-blue-400 transition-colors duration-300 cursor-pointer hover:underline"
            >
              Desenvolvido pela tronos.space
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
